import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ByteMenuContentTypes } from "../byte-hook";
import { IMenuItemResponce, IMenuListItem } from "../../../../../../../Interfaces/responseTypes";
import { ICartItem } from "../../../../../../../Interfaces/componentTypes";
import useDialogHooks from "../../../../../../../Components/Dialog/dialog-hooks";
import { IByteMenuProps } from "./menu";
import MenuItemDialog from "../../Components/Dialogs/MenuItemDialog/menuItemDialog";
import { MenuSearchResult } from "../../../../../../../Components/Inputs/SearchInput/SearchMenuItem/searchMenu-hooks";
import { isBusinessClosed } from "../../../../../../../Utilits/utilits";

const TOP_SPACER_BUFFER = 12;
const TOP_SPACER_BUFFER_CLOSED = 28;

const useMrByteMenu = (props: IByteMenuProps) => {
    const { data, mainCart, currencySymbol, setMainCart } = props;
    const isClosed = isBusinessClosed(data.business_profile);
    const [pageContent, setPageContent] = useState<ByteMenuContentTypes>(ByteMenuContentTypes.CATEGORIES);
    const [selectedCategory, setSelectedCategory] = useState<IMenuListItem | null>(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [dialogComponent, setDialogComponent] = useState<ReactNode | null>(null);
    const [isManualSelection, setIsManualSelection] = useState<boolean>(false);
    const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const categoryRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const welcomeBoxRef = useRef<HTMLDivElement | null>(null);
    const itemHeaderRef = useRef<HTMLDivElement | null>(null);
    const [topSpacerHeight, setTopSpacerHeight] = useState(102);
    const lastCategoryId = data.menu_list[data.menu_list.length - 1].category.id;
    const { open, onOpen, onClose } = useDialogHooks();
    const scrollTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        scrollTop()
    }, []);

    const updateTopSpacerHeight = useCallback(() => {
        const headerEl = pageContent === ByteMenuContentTypes.ITEMS
            ? itemHeaderRef.current
            : welcomeBoxRef.current;

        if (headerEl) {
            const buffer = isClosed ? TOP_SPACER_BUFFER_CLOSED : TOP_SPACER_BUFFER;
            setTopSpacerHeight(headerEl.offsetHeight + buffer);
        }
    }, [isClosed, pageContent]);

    useLayoutEffect(() => {
        updateTopSpacerHeight();
    }, [pageContent, isClosed, data.name, updateTopSpacerHeight]);

    useEffect(() => {
        const headerEl = pageContent === ByteMenuContentTypes.ITEMS
            ? itemHeaderRef.current
            : welcomeBoxRef.current;

        const rafId = requestAnimationFrame(updateTopSpacerHeight);
        const timeoutId = window.setTimeout(updateTopSpacerHeight, 1100);

        if (!headerEl) {
            return () => {
                cancelAnimationFrame(rafId);
                window.clearTimeout(timeoutId);
            };
        }

        const observer = new ResizeObserver(updateTopSpacerHeight);
        observer.observe(headerEl);
        window.addEventListener('resize', updateTopSpacerHeight);

        return () => {
            cancelAnimationFrame(rafId);
            window.clearTimeout(timeoutId);
            observer.disconnect();
            window.removeEventListener('resize', updateTopSpacerHeight);
        };
    }, [pageContent, isClosed, data.name, updateTopSpacerHeight]);

    const handleCategoryClicker = (category: IMenuListItem) => {
        setSelectedCategory(category);
        setIsManualSelection(true);
        setPageContent(ByteMenuContentTypes.ITEMS);

        window.setTimeout(() => {
            updateTopSpacerHeight();

            window.requestAnimationFrame(() => {
                const categoryElement = categoryRefs.current[category.category.id];
                if (categoryElement) {
                    categoryElement.scrollIntoView({ behavior: "smooth", block: "start", inline: "start" });
                } else {
                    scrollTop();
                }
            });
        }, 220);
    };

    useEffect(() => {
        let scrollTimeout: NodeJS.Timeout | null = null;

        const handleScroll = () => {
            if (isManualSelection) {
                return;
            }
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }

            scrollTimeout = setTimeout(() => {
                let closestCategoryId = null;
                let closestDistance = Infinity;

                Object.entries(categoryRefs.current).forEach(([categoryId, element]) => {
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        const distanceFromCenter = Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
                        if (categoryId === String(lastCategoryId)) {
                            if (rect.top < window.innerHeight && rect.bottom > 0) {
                                closestCategoryId = Number(categoryId);
                            }
                        } else {
                            if (distanceFromCenter < closestDistance) {
                                closestDistance = distanceFromCenter;
                                closestCategoryId = Number(categoryId);
                            }
                        }
                    }
                });

                if (closestCategoryId !== null) {
                    setSelectedCategoryId(closestCategoryId);
                }
            }, 200);

        };

        window.addEventListener("scroll", handleScroll);
        const resetManualSelection = setTimeout(() => {
            setIsManualSelection(false);
        }, 500);

        return () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            window.removeEventListener("scroll", handleScroll);
            clearTimeout(resetManualSelection);
        };
    }, [isManualSelection, lastCategoryId]);

    const onCloseDialog = () => {
        setDialogComponent(null);
        onClose();
    };

    const onOpentItemDialog = (category: IMenuListItem, item: IMenuItemResponce) => {
        setDialogComponent(
            <MenuItemDialog
                category={category}
                itemData={item}
                mainCart={mainCart}
                currencySymbol={currencySymbol}
                addToMainCart={addToMainCart}
                onClose={onClose}
            />
        );
        onOpen();
    };

    const addToMainCart = (item: ICartItem) => {
        setMainCart((prevCart) => {
            const itemIndex = prevCart.findIndex((cartItem) => cartItem.id === item.id);
            if (itemIndex !== -1) {
                const updatedCart = [...prevCart];
                const normalizedOptions = (item.option || []).filter((opt) => opt.count > 0);

                if (normalizedOptions.length === 0) {
                    updatedCart.splice(itemIndex, 1);
                } else {
                    updatedCart[itemIndex] = {
                        ...updatedCart[itemIndex],
                        ...item,
                        option: normalizedOptions,
                    };
                }
                return updatedCart;
            } else {
                const normalizedOptions = (item.option || []).filter((opt) => opt.count > 0);
                if (normalizedOptions.length === 0) return prevCart;
                return [...prevCart, { ...item, option: normalizedOptions }];
            }
        });
    };


    const onBack = () => {
        setSelectedCategory(null);
        setPageContent(ByteMenuContentTypes.CATEGORIES);
        scrollTop();
    };

    const onClickSearchResult = (result: MenuSearchResult) => {
        const currentCategory = data.menu_list.find((item) => item.category.id === result.category_id);
        if (currentCategory) {
            setSelectedCategory(currentCategory);
            setPageContent(ByteMenuContentTypes.ITEMS);

            setTimeout(() => {
                if (result.item_id) {
                    const item = currentCategory.items.find((el) => el.id === result.item_id)
                    onOpentItemDialog(currentCategory, item!)
                    const itemElement = itemRefs.current[result.item_id!];
                    if (itemElement) {
                        itemElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "end" });
                    } else {
                        console.warn(`No ref found for item_id=${result.item_id}`);
                    }
                } else {
                    const categoryElement = categoryRefs.current[result.category_id!];
                    if (categoryElement) {
                        categoryElement.scrollIntoView({ behavior: "smooth", block: "start" });
                    } else {
                        scrollTop();
                    }
                }
            }, 100);
        }
    };

    useEffect(() => {
        selectedCategory && setSelectedCategoryId(selectedCategory.category.id);
    }, [selectedCategory]);

    return {
        open,
        itemRefs,
        categoryRefs,
        welcomeBoxRef,
        itemHeaderRef,
        topSpacerHeight,
        pageContent,
        currencySymbol,
        dialogComponent,
        selectedCategory,
        selectedCategoryId,
        onBack,
        onCloseDialog,
        onOpentItemDialog,
        onClickSearchResult,
        handleCategoryClicker,
    }
};

export default useMrByteMenu